import React, { useMemo } from "react";
import { useList, useTranslate } from "@refinedev/core";
import { ViewerSegment, Maybe } from "types/";
import { AnalyticsBarChart, type BarDatasetConfig } from "./AnalyticsBarChart";
import dayjs from "dayjs";
import { buildDateFilters } from "./utils/";
import { analyticsTheme } from "./analyticsTheme";
import { ANALYTICS_STALE_TIME } from "./constants/constants";

const T_PREFIX =
  "media.projects.analytics.dashboard.summaryCards.viewerSegments";

interface ViewerSegmentsChartProps {
  projectId: string | undefined;
  dateRange: {
    startDate: Maybe<dayjs.Dayjs>;
    endDate: Maybe<dayjs.Dayjs>;
  };
  /** Shown next to the title. */
  badge?: React.ReactNode;
}

export const ViewerSegmentsChart: React.FC<ViewerSegmentsChartProps> = ({
  projectId,
  dateRange,
  badge,
}) => {
  const t = useTranslate();
  const {
    data: segmentsData,
    isLoading,
    isFetching,
  } = useList<ViewerSegment>({
    resource: `analytics/projects/${projectId}/analytics/video/viewer-segments`,
    config: {
      filters: buildDateFilters(dateRange),
    },
    queryOptions: {
      enabled: !!projectId && !!dateRange.startDate && !!dateRange.endDate,
      staleTime: ANALYTICS_STALE_TIME,
    },
    errorNotification: false,
  });

  const segments = segmentsData?.data ?? [];

  const labels = useMemo(() => segments.map((s) => s.segment), [segments]);

  const datasets = useMemo<BarDatasetConfig[]>(
    () => [
      {
        label: t(`${T_PREFIX}.sessionPct`),
        data: segments.map((s) => Math.round(s.session_pct * 100) / 100),
        color: analyticsTheme.datasetEmerald,
      },
    ],
    [segments, t]
  );

  return (
    <AnalyticsBarChart
      title={t(`${T_PREFIX}.title`)}
      badge={badge}
      labels={labels}
      datasets={datasets}
      loading={isLoading || isFetching}
      direction="horizontal"
      height={200}
    />
  );
};
